// Graph view of the MCP servers step, from the MCP topic wizard's graph:
// endpoints across, sessions up (square-root scale, so small servers stay
// apart), circle area for tool calls, and each circle split by the agent its
// sessions came from. A red ring marks a server the answer blocks. Selecting
// a circle shows its numbers and its decision control under the graph.
//
// The graph is drawn at the width the chat column gives it, one SVG unit to
// one CSS pixel, so text and strokes keep their size at every column width.
import { useRef, useState } from "react";
import { useElementSize } from "../../../components/kernelPrimitives/shared.jsx";
import { shortToolName } from "../../../lib/mcpServerNames.js";
import { CARD_MUTED } from "../../../components/ChatQuestionCard.jsx";
import { FILE_LABEL } from "../../../components/lenses/triageParts.jsx";
import { CLAUDE, CODEX, COPILOT } from "./changes.js";
import { count, labelOf, rowMeta } from "./cardText.js";

const FALLBACK_WIDTH = 560;
const MIN_WIDTH = 320;
// Room past the plot so the largest circle near an edge is not cut off.
const M = { left: 46, right: 28, top: 30, bottom: 38 };
const FONT = 11;
const CHAR_WIDTH = 6.2;
const LABEL_HEIGHT = 12;
const QUADRANT_FONT = 9;
const QUADRANT_CHAR = 6;
const BLOCK_RING = "#d0603f";
const EDGE = "rgba(0, 0, 0, 0.45)";

/// The plot's size for a width: height in proportion, within bounds.
function frame(width) {
    const W = Math.max(MIN_WIDTH, Math.round(width || FALLBACK_WIDTH));
    const H = Math.round(Math.min(460, Math.max(280, W * 0.58)));
    return { W, H, PW: W - M.left - M.right, PH: H - M.top - M.bottom };
}

export const SLICES = Object.freeze([
    { id: CLAUDE, label: labelOf(CLAUDE), color: "#d9822b" },
    { id: CODEX, label: labelOf(CODEX), color: "#3f7cc4" },
    { id: COPILOT, label: labelOf(COPILOT), color: "#8a6fd1" },
    { id: "other", label: "Other clients", color: "#7d8783" },
]);

function niceCeil(value) {
    if (value <= 1) return 1;
    const power = 10 ** Math.floor(Math.log10(value));
    const step = [1, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10].find((factor) => factor * power >= value);
    return step * power;
}

/// Two significant digits, so ticks on the square-root scale read as round
/// numbers.
function roundTick(value) {
    if (value < 10) return Math.round(value);
    const power = 10 ** (Math.floor(Math.log10(value)) - 1);
    return Math.round(value / power) * power;
}

function boxHitsCircle(box, circle) {
    const nearestX = Math.max(box.left, Math.min(circle.x, box.right));
    const nearestY = Math.max(box.top, Math.min(circle.y, box.bottom));
    return (nearestX - circle.x) ** 2 + (nearestY - circle.y) ** 2 < (circle.r + 2) ** 2;
}

const boxesOverlap = (a, b) => a.left < b.right && b.left < a.right && a.top < b.bottom && b.top < a.bottom;

/// Place each name to the right, left, above, or below its circle, the
/// busiest servers first, clear of `taken` boxes (the quadrant names). A name
/// that fits nowhere is left out; selecting the circle still names it.
function placeLabels(circles, { W, PH }, taken) {
    const placed = [...taken];
    return circles.map((circle) => {
        const width = circle.row.name.length * CHAR_WIDTH;
        const candidates = [
            { x: circle.x + circle.r + 4, y: circle.y + 3, anchor: "start", left: circle.x + circle.r + 4 },
            { x: circle.x - circle.r - 4, y: circle.y + 3, anchor: "end", left: circle.x - circle.r - 4 - width },
            { x: circle.x, y: circle.y - circle.r - 4, anchor: "middle", left: circle.x - width / 2 },
            { x: circle.x, y: circle.y + circle.r + 12, anchor: "middle", left: circle.x - width / 2 },
        ];
        for (const candidate of candidates) {
            const box = { left: candidate.left, right: candidate.left + width, top: candidate.y - LABEL_HEIGHT + 2, bottom: candidate.y + 2 };
            if (box.left < M.left || box.right > W - M.right || box.top < M.top || box.bottom > M.top + PH) continue;
            if (placed.some((other) => boxesOverlap(box, other))) continue;
            if (circles.some((other) => other !== circle && boxHitsCircle(box, other))) continue;
            placed.push(box);
            return { ...circle, label: candidate };
        }
        return { ...circle, label: null };
    });
}

function arc(cx, cy, r, from, to) {
    const point = (turn) => [cx + r * Math.sin(turn * 2 * Math.PI), cy - r * Math.cos(turn * 2 * Math.PI)];
    const [x0, y0] = point(from);
    const [x1, y1] = point(to);
    return `M ${cx} ${cy} L ${x0} ${y0} A ${r} ${r} 0 ${to - from > 0.5 ? 1 : 0} 1 ${x1} ${y1} Z`;
}

/// Sessions for each slice, in slice order, without empty slices.
export function sessionSlices(row) {
    return SLICES.map((slice) => ({ slice, count: Number(row.sessionsBy?.[slice.id] || 0) })).filter((s) => s.count > 0);
}

function Bubble({ circle, blocked, selected, onSelect }) {
    const { row, x, y, r } = circle;
    const slices = sessionSlices(row);
    const total = slices.reduce((sum, s) => sum + s.count, 0);
    let turn = 0;
    const parts = slices.map(({ slice, count: n }) => {
        const from = turn;
        turn += n / total;
        return { slice, from, to: turn };
    });
    const select = () => onSelect(selected ? null : row.id);
    return (
        <g
            role="button"
            tabIndex={0}
            aria-pressed={selected}
            aria-label={`${row.name}: ${count(row.endpoints, "endpoint", "endpoints")}, ${count(row.sessions, "session", "sessions")}, ${count(row.calls, "tool call", "tool calls")}${blocked ? ", blocked" : ""}`}
            className="cursor-pointer outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-[color:var(--line-strong)]"
            onClick={select}
            onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    select();
                }
            }}
        >
            {parts.length <= 1
                ? <circle cx={x} cy={y} r={r} fill={parts[0]?.slice.color ?? SLICES[3].color} fillOpacity="0.9" />
                : parts.map((part) => <path key={part.slice.id} d={arc(x, y, r, part.from, part.to)} fill={part.slice.color} fillOpacity="0.9" />)}
            <circle cx={x} cy={y} r={r} fill="none" stroke={EDGE} strokeWidth="1" />
            {blocked ? <circle cx={x} cy={y} r={r + 3} fill="none" stroke={BLOCK_RING} strokeWidth="2" /> : null}
            {selected ? <circle cx={x} cy={y} r={r + (blocked ? 7 : 5)} fill="none" stroke="var(--ink)" strokeWidth="1" strokeDasharray="3 3" /> : null}
        </g>
    );
}

const ENDPOINTS_SHOWN = 2;
const MORE = "w-fit cursor-pointer border-0 bg-transparent p-0 text-[11px] text-[color:var(--muted)] underline decoration-dotted underline-offset-2 hover:text-[color:var(--ink)]";

/// The busiest two endpoints, and a button for the rest.
function EndpointList({ endpoints }) {
    const [open, setOpen] = useState(false);
    if (!endpoints || endpoints.state === "loading") return <span className={CARD_MUTED}>Reading the endpoints.</span>;
    if (endpoints.state === "error") return <span className={CARD_MUTED}>The endpoints could not be read.</span>;
    if (!endpoints.rows.length) return <span className={CARD_MUTED}>No endpoint called this server in the last 30 days.</span>;
    const rest = endpoints.rows.length - ENDPOINTS_SHOWN;
    const shown = open ? endpoints.rows : endpoints.rows.slice(0, ENDPOINTS_SHOWN);
    return (
        <>
            {shown.map((e) => (
                <span key={e.id} className="grid gap-[1px] py-[2px]">
                    <span className="break-all font-mono text-[11px] text-[color:var(--ink)]">{e.name}</span>
                    <span className="text-[11px] text-[color:var(--muted)]">{rowMeta(e)}</span>
                </span>
            ))}
            {rest > 0 ? (
                <button type="button" aria-expanded={open} className={MORE} onClick={() => setOpen(!open)}>
                    {open ? "Show fewer" : `Show ${rest} more`}
                </button>
            ) : null}
        </>
    );
}

function endpointsLabel(row, endpoints) {
    const read = endpoints?.rows?.length ?? 0;
    if (endpoints?.state !== "ready") return "Endpoints";
    return read < row.endpoints ? `Endpoints · ${row.endpoints.toLocaleString()}, busiest ${read} listed` : `Endpoints · ${read}`;
}

/// The numbers behind one circle, its endpoints, and its decision control.
function ServerDetail({ row, control, endpoints }) {
    const slices = sessionSlices(row);
    const total = slices.reduce((sum, s) => sum + s.count, 0);
    return (
        <span className="grid gap-[6px] border-0 border-t border-solid border-[color:var(--line-soft)] px-[4px] pt-[8px]">
            <span className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-[10px]">
                <span className="min-w-0 break-all font-mono text-[12px] text-[color:var(--ink)]">{row.name}</span>
                {control}
            </span>
            <span className="text-[11px] leading-[1.4] text-[color:var(--muted)]">
                {`${count(row.endpoints, "endpoint", "endpoints")}, ${count(row.sessions, "session", "sessions")}, ${count(row.calls, "tool call", "tool calls")}`}
                {row.sessions ? `, ${(row.calls / row.sessions).toFixed(1)} per session` : ""}
            </span>
            {slices.length ? (
                <span className="flex flex-wrap gap-x-[12px] gap-y-[2px] text-[11px] text-[color:var(--muted)]">
                    {slices.map(({ slice, count: n }) => (
                        <span key={slice.id} className="flex items-center gap-[5px]">
                            <span aria-hidden="true" className="h-[8px] w-[8px]" style={{ background: slice.color }} />
                            {`${slice.label} ${n.toLocaleString()} (${Math.round((n / total) * 100)}%)`}
                        </span>
                    ))}
                </span>
            ) : null}
            <span className="grid gap-[1px]">
                <span className={FILE_LABEL}>{endpointsLabel(row, endpoints)}</span>
                <EndpointList key={row.id} endpoints={endpoints} />
            </span>
            {row.tools?.length ? (
                <span className="grid gap-[1px]">
                    <span className={FILE_LABEL}>Busiest tools</span>
                    {row.tools.map((t) => (
                        <span key={t.tool} className="flex justify-between gap-[10px] text-[11px] text-[color:var(--muted)]">
                            <span className="break-all">{shortToolName(t.tool)}</span>
                            <span className="font-mono tabular-nums">{t.calls.toLocaleString()}</span>
                        </span>
                    ))}
                </span>
            ) : null}
        </span>
    );
}

/**
 * `rows` are mcp_servers source rows; `isBlocked(id)` says whether the answer
 * blocks a server; `renderControl(row)` gives the selected server's decision
 * control; `selectedId` and `onSelect` hold the selection; `endpoints` is the
 * selected server's endpoints read, `{ state, rows }`.
 */
export default function McpServerGraph({ rows, isBlocked, renderControl, selectedId, onSelect, endpoints = null }) {
    const boxRef = useRef(null);
    const { width } = useElementSize(boxRef);
    const used = rows.filter((r) => r.calls > 0 || r.sessions > 0);
    if (!used.length) return <span className={CARD_MUTED}>No MCP tool calls in the last 30 days.</span>;
    const selected = used.find((r) => r.id === selectedId) ?? null;
    const f = frame(width);
    const { W, H, PW, PH } = f;
    const xMax = niceCeil(Math.max(...used.map((r) => r.endpoints)));
    const yMax = niceCeil(Math.max(...used.map((r) => r.sessions)));
    const maxCalls = Math.max(1, ...used.map((r) => r.calls));
    const x = (value) => M.left + (value / xMax) * PW;
    const maxRadius = Math.min(26, Math.max(14, PW * 0.035));
    // The busiest server stays a circle's height below the top quadrant names.
    const span = PH - maxRadius - 6;
    const y = (value) => M.top + PH - (Math.sqrt(value) / Math.sqrt(yMax)) * span;
    const radius = (calls) => 3.5 + Math.sqrt(calls / maxCalls) * maxRadius;
    const xTicks = [...new Set([0, 0.25, 0.5, 0.75, 1].map((share) => roundTick(share * xMax)))];
    const yTicks = [...new Set([0, 1 / 16, 1 / 4, 9 / 16, 1].map((share) => roundTick(share * yMax)))];
    const midX = x(xMax / 2);
    const midY = y(yMax / 4);
    const placedCircles = [...used].sort((a, b) => b.calls - a.calls).map((row) => ({ row, x: x(row.endpoints), y: y(row.sessions), r: radius(row.calls) }));
    // The top quadrant names sit above the plot, where no circle reaches. The
    // bottom ones sit in the plot's corners and are left out when a circle
    // covers them.
    const quadrants = [
        { text: "Busy on few endpoints", x: M.left + 4, y: M.top - 10, anchor: "start" },
        { text: "Widespread and busy", x: W - M.right, y: M.top - 10, anchor: "end" },
        { text: "Niche", x: M.left + 6, y: M.top + PH - 6, anchor: "start" },
        { text: "Wide but light use", x: W - M.right - 6, y: M.top + PH - 6, anchor: "end" },
    ].map((q) => {
        const w = q.text.length * QUADRANT_CHAR;
        const left = q.anchor === "end" ? q.x - w : q.x;
        return { ...q, box: { left, right: left + w, top: q.y - 10, bottom: q.y + 2 } };
    }).filter((q) => q.y < M.top || !placedCircles.some((c) => boxHitsCircle(q.box, c)));
    const circles = placeLabels(placedCircles, f, quadrants.map((q) => q.box));
    return (
        <span ref={boxRef} className="grid gap-[6px]">
            <span className="flex flex-wrap items-center gap-x-[12px] gap-y-[2px] text-[11px] text-[color:var(--muted)]">
                {SLICES.filter((slice) => used.some((r) => r.sessionsBy?.[slice.id])).map((slice) => (
                    <span key={slice.id} className="flex items-center gap-[5px]">
                        <span aria-hidden="true" className="h-[8px] w-[8px]" style={{ background: slice.color }} />
                        {slice.label}
                    </span>
                ))}
                <span>Circle area: tool calls</span>
                <span className="flex items-center gap-[5px]">
                    <span aria-hidden="true" className="h-[9px] w-[9px] rounded-full border-2 border-solid" style={{ borderColor: BLOCK_RING }} />
                    Blocked
                </span>
            </span>
            <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} className="block h-auto max-w-full" shapeRendering="geometricPrecision" role="group" aria-label="MCP servers by endpoints, sessions, and tool calls. Select a circle for its numbers.">
                <line x1={M.left} y1={M.top + PH} x2={W - M.right} y2={M.top + PH} stroke="var(--line-strong)" />
                <line x1={M.left} y1={M.top} x2={M.left} y2={M.top + PH} stroke="var(--line-strong)" />
                <line x1={midX} y1={M.top} x2={midX} y2={M.top + PH} stroke="var(--line)" strokeDasharray="4 4" />
                <line x1={M.left} y1={midY} x2={W - M.right} y2={midY} stroke="var(--line)" strokeDasharray="4 4" />
                <g aria-hidden="true">
                    {quadrants.map((q) => <text key={q.text} x={q.x} y={q.y} textAnchor={q.anchor} fontSize={QUADRANT_FONT} letterSpacing="0.06em" fill="var(--eyebrow)">{q.text.toUpperCase()}</text>)}
                    {xTicks.map((tick) => <text key={`x${tick}`} x={x(tick)} y={M.top + PH + 15} textAnchor="middle" fontSize={FONT} fill="var(--muted)">{tick.toLocaleString()}</text>)}
                    {yTicks.map((tick) => <text key={`y${tick}`} x={M.left - 6} y={y(tick) + 3} textAnchor="end" fontSize={FONT} fill="var(--muted)">{tick.toLocaleString()}</text>)}
                    <text x={M.left + PW / 2} y={H - 4} textAnchor="middle" fontSize={FONT} fill="var(--muted)">Endpoints that called the server</text>
                    <text x={12} y={M.top + PH / 2} textAnchor="middle" fontSize={FONT} fill="var(--muted)" transform={`rotate(-90 12 ${M.top + PH / 2})`}>Sessions</text>
                </g>
                {circles.map((circle) => <Bubble key={circle.row.id} circle={circle} blocked={isBlocked(circle.row.id)} selected={circle.row.id === selectedId} onSelect={onSelect} />)}
                {circles.map((circle) => (circle.label ? (
                    <text
                        key={circle.row.id}
                        x={circle.label.x}
                        y={circle.label.y}
                        textAnchor={circle.label.anchor}
                        fontSize={FONT}
                        fill="var(--ink)"
                        fontWeight={circle.row.id === selectedId ? 600 : 400}
                        aria-hidden="true"
                        className="cursor-pointer"
                        onClick={() => onSelect(circle.row.id === selectedId ? null : circle.row.id)}
                    >
                        {circle.row.name}
                    </text>
                ) : null))}
            </svg>
            {selected ? <ServerDetail row={selected} control={renderControl(selected)} endpoints={endpoints} /> : <span className={CARD_MUTED}>Select a circle to see its numbers and block it.</span>}
        </span>
    );
}
